import type { Community, NavigationItemId, UserWithCommunities } from '@/types';

/** Mirrors Prisma default and DashboardShell fallback when no config exists. */
export const DEFAULT_COMMUNITY_NAV_ITEM_IDS: NavigationItemId[] = [
  'home',
  'members',
  'events',
  'create',
  'business',
  'profile',
];

/** Community events list → featured event detail (slug or id). */
export const COMMUNITY_EVENTS_LANDING_EVENT: Record<string, string> = {
  '1rAaiFKbII': 'mahasangam-event-network-to-networth-b9fyaz9hth',
};

/** Override Events tab label for communities with a featured live event. */
export const COMMUNITY_EVENTS_NAV_LABEL: Record<string, string> = {
  '1rAaiFKbII': 'Live event',
};

const GUEST_NAV_ALLOWLIST = new Set<NavigationItemId>(['home', 'events', 'business']);

export function communityEventsPath(communityId: string): string {
  const landingEvent = COMMUNITY_EVENTS_LANDING_EVENT[communityId];
  return landingEvent
    ? `/${communityId}/events/${landingEvent}`
    : `/${communityId}/events`;
}

export function communityEventsNavLabel(communityId: string): string {
  return COMMUNITY_EVENTS_NAV_LABEL[communityId] ?? 'Events';
}

/**
 * Same ordering / filtering logic as DashboardShell previously inlined:
 * configured items → optional guest filter → strip `projects` when feature off.
 */
export function getEffectiveShellNavIds(
  community: Pick<
    Community,
    'navigationConfig' | 'isProjectsEnabled' | 'isBusinessDirectoryEnabled' | 'isGivesAsksEnabled' | 'kind'
  >,
  user: Pick<UserWithCommunities, 'id'> | null | undefined
): NavigationItemId[] {
  if (community.kind === 'AGGREGATOR') {
    return ['home'];
  }

  const configured =
    community.navigationConfig?.items && community.navigationConfig.items.length > 0
      ? community.navigationConfig.items
      : [...DEFAULT_COMMUNITY_NAV_ITEM_IDS];

  let filtered: NavigationItemId[] = configured;
  if (!user) {
    filtered = configured.filter((id) => GUEST_NAV_ALLOWLIST.has(id));
  }
  const showProjects = community.isProjectsEnabled;
  const showBusinessDirectory = community.isBusinessDirectoryEnabled;
  const visible = filtered.filter((itemId) => {
    if (itemId === 'gives-asks') return false;
    if (itemId === 'projects' && !showProjects) return false;
    if (itemId === 'business' && !showBusinessDirectory) return false;
    return true;
  });
  if (!user || !community.isGivesAsksEnabled) return visible;
  const directoryIndex = visible.indexOf('business');
  const insertAt = directoryIndex === -1 ? visible.length : directoryIndex + 1;
  return [...visible.slice(0, insertAt), 'gives-asks', ...visible.slice(insertAt)];
}

export function navigationItemPath(communityId: string, itemId: NavigationItemId): string {
  if (itemId === 'home') return `/${communityId}/dashboard`;
  if (itemId === 'events') return communityEventsPath(communityId);
  return `/${communityId}/${itemId}`;
}

/** First visible tab path for switches / redirects after login. Fallback: dashboard (route still exists). */
export function resolveCommunityLandingPath(
  community: Pick<
    Community,
    'id' | 'navigationConfig' | 'isProjectsEnabled' | 'isBusinessDirectoryEnabled' | 'isGivesAsksEnabled' | 'kind'
  >,
  user: Pick<UserWithCommunities, 'id'> | null | undefined
): string {
  if (community.kind === 'AGGREGATOR') {
    return `/${community.id}/dashboard`;
  }
  const ids = getEffectiveShellNavIds(community, user);
  const primary = ids[0];
  if (!primary) {
    return `/${community.id}/dashboard`;
  }
  return navigationItemPath(community.id, primary);
}
